import { useMemo, useState } from 'react';
import { Bell, Check, Loader2, Send } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { TimelineUnificada } from '@/components/timeline/TimelineUnificada';
import { SeletorConsultor } from '@/components/carteira/SeletorConsultor';
import { useConsultoresElegiveis } from '@/hooks/useConsultoresElegiveis';
import {
  useAbrirChamadoCarteira,
  useChamadosCarteira,
  useResponderChamadoCarteira,
} from '@/hooks/useCarteiraChamados';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

/**
 * A TIMELINE DO CLIENTE, dentro da carteira — e o chamado mora nela.
 *
 * O fio (Douglas 02/09/2026): a timeline é o LUGAR, o aviso é só a batida na
 * porta. Quem pede escreve aqui e escolhe quem atende; quem recebe o aviso cai
 * de volta nesta mesma tela, lê e responde no mesmo campo. Responder é
 * resolver — não existe botão separado de "concluir", porque concluir sem
 * dizer o que foi feito não conta.
 */

const CARD = 'rounded-xl border border-border bg-card shadow-[0_1px_2px_rgba(60,20,30,0.05),0_4px_14px_-6px_rgba(60,20,30,0.08)]';
const ROTULO = 'text-[10px] font-bold uppercase tracking-wider text-[#6B5A4D] dark:text-[#B5A8A8]';
const BOTAO_PRIMARIO =
  'h-[30px] shrink-0 gap-1.5 border-0 bg-[linear-gradient(90deg,#4A1320,#5B0A0D)] px-3 text-[11px] font-bold text-white transition-all hover:brightness-110 dark:bg-[linear-gradient(90deg,#9D2530,#6C2236)]';

const formatarCnpj = (valor: string) => {
  const d = (valor || '').replace(/\D/g, '');
  if (d.length !== 14) return valor;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

const quando = (iso: string) => {
  const data = new Date(iso);
  return data.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cnpj: string;
  clienteId?: string | null;
  razaoSocial?: string | null;
}

export function CarteiraTimelineSheet({ open, onOpenChange, cnpj, clienteId, razaoSocial }: Props) {
  const { profile } = useAuth();
  const { toast } = useToast();
  const digitos = (cnpj || '').replace(/\D/g, '');

  const [texto, setTexto] = useState('');
  const [destino, setDestino] = useState<string | null>(null);
  const [resposta, setResposta] = useState<Record<string, string>>({});

  // "Qualquer um do CRM" (Douglas 02/09/2026): quem vê um cliente parado pode
  // chamar quem resolve, sem passar pela gestão.
  const { data: pessoas = [], isLoading: carregandoPessoas } = useConsultoresElegiveis({
    escopo: 'todos',
    excluirId: profile?.id ?? null,
    habilitado: open,
  });

  const { data: chamados = [], isLoading: carregandoChamados } = useChamadosCarteira(open ? digitos : null);
  const abrir = useAbrirChamadoCarteira(digitos);
  const responder = useResponderChamadoCarteira(digitos);

  const meus = useMemo(
    () => chamados.filter((c) => c.responsavel_id === profile?.id),
    [chamados, profile?.id],
  );
  const dosOutros = useMemo(
    () => chamados.filter((c) => c.responsavel_id !== profile?.id),
    [chamados, profile?.id],
  );

  const handleAbrir = () => {
    if (!destino || !texto.trim()) return;
    abrir.mutate(
      { destino_usuario_id: destino, texto: texto.trim(), cliente_id: clienteId ?? null },
      {
        onSuccess: (payload) => {
          const dados = payload?.data;
          const nome = dados && !Array.isArray(dados) ? dados.destino : null;
          toast({
            title: 'Chamado aberto',
            description: `${nome || 'A pessoa'} foi avisada e o pedido está na timeline deste cliente.`,
          });
          setTexto('');
          setDestino(null);
        },
        onError: (erro: Error) => {
          toast({ title: 'O chamado não foi aberto', description: erro.message, variant: 'destructive' });
        },
      },
    );
  };

  const handleResponder = (chamadoId: string) => {
    const conteudo = (resposta[chamadoId] || '').trim();
    if (!conteudo) return;
    responder.mutate(
      { chamadoId, texto: conteudo },
      {
        onSuccess: () => {
          toast({ title: 'Chamado atendido', description: 'A resposta entrou na timeline e o aviso saiu da sua tela.' });
          setResposta((atual) => ({ ...atual, [chamadoId]: '' }));
        },
        onError: (erro: Error) => {
          toast({ title: 'A resposta não foi salva', description: erro.message, variant: 'destructive' });
        },
      },
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* O X do Sheet nasce escuro; sobre o hero vinho ele some. */}
      <SheetContent className="flex h-full w-full flex-col gap-0 p-0 sm:max-w-3xl [&>button]:text-white/70 [&>button]:hover:text-white">
        <SheetHeader className="shrink-0 border-b border-white/[0.07] bg-[linear-gradient(150deg,#5E1424,#3E0E1B)] py-3 pl-4 pr-12">
          <SheetTitle className="truncate text-[15px] font-bold text-white">
            {razaoSocial || formatarCnpj(digitos)}
          </SheetTitle>
          <p className="text-[11px] text-white/[0.62]">
            {formatarCnpj(digitos)} · tudo que já aconteceu com este cliente
          </p>
        </SheetHeader>

        <div className="flex-1 min-h-0 space-y-3 overflow-y-auto p-4">
          {/* MEUS CHAMADOS — a dívida é minha, então vem primeiro e em bordô. */}
          {meus.map((chamado) => (
            <div key={chamado.id} className={cn(CARD, 'rounded-l-none border-l-[3px] border-l-[#5B0A0D] p-3')}>
              <div className="flex items-baseline justify-between gap-2">
                <span className={ROTULO}>Chamado para você</span>
                <span className="text-[10px] text-[#6B5A4D] dark:text-[#B5A8A8]">
                  {chamado.autor_nome || 'Alguém'} · {quando(chamado.created_at)}
                </span>
              </div>
              <p className="mt-1.5 whitespace-pre-wrap text-[13px] text-foreground">{chamado.texto}</p>
              <Textarea
                value={resposta[chamado.id] || ''}
                onChange={(e) => setResposta((atual) => ({ ...atual, [chamado.id]: e.target.value }))}
                placeholder="O que você fez com este cliente…"
                className="mt-2 min-h-[56px] resize-none text-xs"
              />
              <div className="mt-2 flex items-center justify-between gap-2">
                <p className="text-[10px] text-[#6B5A4D] dark:text-[#B5A8A8]">
                  Responder encerra o chamado e tira o aviso da sua tela.
                </p>
                <Button
                  type="button"
                  onClick={() => handleResponder(chamado.id)}
                  disabled={responder.isPending || !(resposta[chamado.id] || '').trim()}
                  className={BOTAO_PRIMARIO}
                >
                  {responder.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                  Responder e encerrar
                </Button>
              </div>
            </div>
          ))}

          {/* Chamado de outra pessoa: informa, não cobra — sem filete. */}
          {dosOutros.map((chamado) => (
            <div key={chamado.id} className={cn(CARD, 'p-3')}>
              <div className="flex items-baseline justify-between gap-2">
                <span className={ROTULO}>Chamado aberto</span>
                <span className="text-[10px] text-[#6B5A4D] dark:text-[#B5A8A8]">
                  {chamado.autor_nome || 'Alguém'} → {chamado.responsavel_nome || '—'} · {quando(chamado.created_at)}
                </span>
              </div>
              <p className="mt-1.5 whitespace-pre-wrap text-[13px] text-foreground">{chamado.texto}</p>
            </div>
          ))}

          {/* ABRIR CHAMADO */}
          <div className={cn(CARD, 'p-3')}>
            <div className="mb-2 flex items-center gap-1.5">
              <Bell className="h-3.5 w-3.5 text-[#5B0A0D] dark:text-[#C87484]" />
              <span className={ROTULO}>Chamar alguém para atender</span>
            </div>
            <SeletorConsultor
              consultores={pessoas}
              valor={destino}
              onEscolher={(id: string) => setDestino(id)}
              carregando={carregandoPessoas}
              placeholder="Quem vai atender este cliente"
              alturaLista={200}
            />
            <Textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="O que precisa ser feito com este cliente…"
              className="mt-2 min-h-[64px] resize-none text-xs"
            />
            <div className="mt-2 flex items-center justify-between gap-2">
              <p className="text-[10px] text-[#6B5A4D] dark:text-[#B5A8A8]">
                Fica na timeline deste cliente e vira aviso para quem você escolher.
              </p>
              <Button
                type="button"
                onClick={handleAbrir}
                disabled={abrir.isPending || !destino || !texto.trim()}
                className={BOTAO_PRIMARIO}
              >
                {abrir.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Abrir chamado
              </Button>
            </div>
          </div>

          {carregandoChamados && (
            <p className="text-[11px] text-[#6B5A4D] dark:text-[#B5A8A8]">Carregando chamados…</p>
          )}

          <TimelineUnificada cnpj={digitos} clienteId={clienteId ?? undefined} />
        </div>
      </SheetContent>
    </Sheet>
  );
}
